import { ensurePresent } from "./utils";
import { TCF_CONSENT_IDS, TCF_VENDOR_ID } from "./config";
import { type Logger } from "./logger";
import { type DqTagConsentPayload, type TCData } from "./models";

export class ConsentProcessor {
  private _disabled: boolean;
  private _tcfData: TCData | null;
  private _loaded = false;

  constructor(private _logger: Logger) {
    this._disabled = false;
    this._tcfData = null;
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get applied(): boolean {
    return Boolean(this._tcfData?.gdprApplies);
  }

  get loaded(): boolean {
    return this._loaded;
  }

  get hasConsentOrNotApplied(): boolean {
    if (this.disabled || !this.applied) {
      return true;
    }
    const hasRequiredVendorConsents =
      TCF_VENDOR_ID in (this._tcfData?.vendor?.consents ?? {});
    const hasRequiredPurposeConsents = TCF_CONSENT_IDS.every(
      (id) => this._tcfData?.purpose?.consents[id],
    );
    return hasRequiredPurposeConsents && hasRequiredVendorConsents;
  }

  get payload(): DqTagConsentPayload {
    return {
      disableTcf: this.disabled,
      tcf: this.disabled ? undefined : this._tcfData?.tcString,
      gdprApplies: this.disabled ? undefined : this.applied,
    };
  }

  public async initialize(): Promise<void> {
    if (this.disabled) {
      return;
    }
    const tcf = await ensurePresent(() => window["__tcfapi"], 5000);
    if (!tcf) {
      this._loaded = true;
      this._logger.printError("TCF API is not available");
      return;
    }
    tcf("getTCData", 2, (tcData: TCData, success: boolean) => {
      this._logger.printDebug("TC data received:", tcData);
      if (
        tcData.eventStatus !== "tcloaded" &&
        tcData.eventStatus !== "useractioncomplete" &&
        tcData.eventStatus !== "tcstringupdated" &&
        tcData.gdprApplies
      ) {
        return;
      }
      this._tcfData = success ? tcData : null;
      if (!success) {
        this._logger.printError(
          "TCF data could not be loaded or is invalid",
          tcData,
        );
      }
      this._loaded = true;
    });
    tcf("addEventListener", 2, (tcData: TCData, success: boolean) => {
      this._logger.printDebug("TC event received:", tcData);
      if (
        tcData.eventStatus !== "tcloaded" &&
        tcData.eventStatus !== "useractioncomplete" &&
        tcData.eventStatus !== "tcstringupdated"
      ) {
        return;
      }
      this._tcfData = success ? tcData : null;
      if (!success) {
        this._logger.printError(
          "TCF data could not be loaded or is invalid",
          tcData,
        );
      }
      this._loaded = true;
    });
  }
}
